import type { ReactNode } from 'react';

import { UnverifiedIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';

type AssetListItemContentProps = {
  primary: ReactNode;
  /** Optional — a nullish secondary renders no second line at all. */
  secondary?: ReactNode;
  /**
   * Optional element rendered at the end of the primary line (e.g. the USD
   * balance). Line-end slots let each line truncate against its own end
   * content — the primary text runs until only the row gap (20px) remains
   * before the balance, instead of being squeezed by the widest line of a
   * side column.
   */
  primaryEnd?: ReactNode;
  /** Optional element rendered at the end of the secondary line. */
  secondaryEnd?: ReactNode;
  isDisabled?: boolean;
  isUnofficial?: boolean;
};

export function AssetListItemContent({
  primary,
  secondary,
  primaryEnd,
  secondaryEnd,
  isDisabled,
  isUnofficial,
}: AssetListItemContentProps) {
  return (
    <div className="flex min-w-0 grow flex-col items-start gap-y-0.5 text-left">
      <div className="flex w-full items-center justify-between gap-x-5">
        <span
          className={cn(
            'text-emphasis flex min-w-0 items-center gap-1.5 leading-tight font-semibold',
            isDisabled && 'text-disabled'
          )}
        >
          <span className="truncate">{primary}</span>
          {isUnofficial && (
            <UnverifiedIcon
              size="2xsmall"
              className="fill-secondary shrink-0"
            />
          )}
        </span>

        {primaryEnd != null && <span className="shrink-0">{primaryEnd}</span>}
      </div>

      {(secondary != null || secondaryEnd != null) && (
        <div className="flex w-full items-center justify-between gap-x-5">
          <span
            className={cn(
              'text-secondary min-w-0 truncate text-sm/tight font-semibold',
              isDisabled && 'text-disabled'
            )}
          >
            {secondary}
          </span>

          {secondaryEnd != null && (
            <span className="shrink-0">{secondaryEnd}</span>
          )}
        </div>
      )}
    </div>
  );
}
